/**
 * One driver's slice of the route map data, for the phone driver map (#2679).
 * The map's `selectedDriverId` only dims the other drivers, so drawing one
 * driver means handing the map one driver's data.
 */
import { useMemo, useRef } from 'react';
import type { RouteMonitoringJoinResult } from '../hooks/useRouteMonitoringJoin';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';

export function joinForDriver(
  join: RouteMonitoringJoinResult,
  workerId: string,
): RouteMonitoringJoinResult {
  return {
    drivers: join.drivers.filter((driver) => driver.workerId === workerId),
    stopsGeoJson: {
      ...join.stopsGeoJson,
      features: join.stopsGeoJson.features.filter(
        (feature) => feature.properties.driver_id === workerId,
      ),
    },
    linesGeoJson: {
      ...join.linesGeoJson,
      features: join.linesGeoJson.features.filter(
        (feature) => feature.properties.driver_id === workerId,
      ),
    },
    driverLocations: join.driverLocations.filter((location) => location.workerId === workerId),
    unassignedStops: [],
  };
}

/**
 * The map re-frames whenever its `stops` array changes identity, and every
 * 60s ETA poll brings a new join (and a stops refetch may bring a new day's
 * array that only moved other drivers). So the driver's stops keep their
 * identity for as long as the driver's job ids stay the same; otherwise each
 * poll would pull the viewport back from wherever the user panned.
 */
export function useDriverMapData(
  stops: RoutePlanningStop[],
  join: RouteMonitoringJoinResult,
  workerId: string,
): { stops: RoutePlanningStop[]; join: RouteMonitoringJoinResult } {
  const driverJoin = useMemo(() => joinForDriver(join, workerId), [join, workerId]);
  const jobIdsKey = driverJoin.stopsGeoJson.features
    .map((feature) => feature.properties.job_id)
    .sort()
    .join('|');
  const lastRef = useRef<{ key: string; stops: RoutePlanningStop[] } | null>(null);
  if (lastRef.current === null || lastRef.current.key !== jobIdsKey) {
    const ids = new Set(jobIdsKey === '' ? [] : jobIdsKey.split('|'));
    lastRef.current = { key: jobIdsKey, stops: stops.filter((stop) => ids.has(stop.job_id)) };
  }
  return { stops: lastRef.current.stops, join: driverJoin };
}
